import cn from "classnames"
import { Fragment, useMemo } from "react"

import { Button, Flex, Link, Skeleton, Text } from "@/atoms"
import { removeCommonParamsQueryString } from "@/helpers/filters.helper"
import { getStringForCompare } from "@/helpers/string.helpers"

import styles from "./StatisticWidget.module.scss"

import type { StatisticWidgetProps } from "."

export const StatisticWidget = (props: StatisticWidgetProps) => {
  const {
    title,
    value,
    percentage,
    icon,
    onClick,
    border,
    flex,
    loading,
    to,
    alignValueBottom,
    className
  } = props
  const isClickable = !!onClick
  const Wrapper = isClickable ? Button : "div"
  const isPercentage = typeof percentage === "number"
  const LinkComponent = to ? Link : Fragment
  const propsLinkComponent = to ? { to } : {}
  const symbol = "&"
  const search = window.location.search

  const isActive = useMemo(
    () =>
      !!to &&
      getStringForCompare(`${to.split("?")[1] || ""}`, symbol) ===
        getStringForCompare(removeCommonParamsQueryString(search), symbol),
    [to, search]
  )

  return (
    <Wrapper
      className={cn(
        {
          [styles.container]: !isClickable,
          [styles.containerBorder]: border,
          [styles.containerFlex]: flex,
          [styles.containerWithoutIcon]: !icon,
          [styles.containerLink]: to,
          [styles.containerLinkActive]: isActive
        },
        className
      )}
      onClick={onClick}
    >
      <LinkComponent {...propsLinkComponent}>
        <Flex
          align={alignValueBottom ? "stretch" : "center"}
          justify="space-between"
          className={cn(alignValueBottom && styles.fullHeight)}
        >
          <Flex vertical flex={1} justify="space-between">
            <Text
              className={cn(styles.title, border && styles.titleBorderVariant)}
              type="secondary"
            >
              {title}
            </Text>
            <Flex
              flex={alignValueBottom ? "none" : 1}
              align="center"
              justify="space-between"
              className={cn(loading && styles.skeleton)}
            >
              {loading && (
                <>
                  <Skeleton.Input active size="small" />
                  {isPercentage && <Skeleton.Button active size="small" />}
                </>
              )}
              {!loading && (
                <>
                  <Text className={cn(styles.value, styles.valueBorderVariant)}>
                    {value}
                  </Text>
                  {isPercentage && (
                    <Text
                      type="secondary"
                      className={styles.percentage}
                    >{`(${percentage}%)`}</Text>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {icon && (
            <Flex
              align="center"
              justify="center"
              className={cn(
                styles.iconWrapper,
                alignValueBottom && styles.iconWrapperCentered
              )}
            >
              {icon}
            </Flex>
          )}
        </Flex>
      </LinkComponent>
    </Wrapper>
  )
}
